import { observable } from "mobx";
import { ICalc, IEmpresa, IRelatorios, newEmpresa, newIRelatorios } from "../../model";
import { api } from "../../api";
import { toast } from "react-toastify";
import moment from "moment";
import { decodeUtf8String, downloadBinaryFile, sortBy } from "../../utils";

export class _RelatorioStore {
  obs = observable({
    open_initial: false as boolean,
    selectedEmpresa: newEmpresa() as IEmpresa,
    modelo: "" as string,
    tipo: "" as string,
    relatorio: [] as any,
    tasks_relatorio: [] as any[],
    length_tasks_relatorio: 0 as number,
    date_start: "" as string,
    date_end: "" as string,
    relatorios: [] as any,
    select_relatorio: newIRelatorios() as IRelatorios,
    columns: [] as any,
    loading: false as boolean,
    open_erros_relatorio: false as boolean,
    open_relatorios_salvos: false as boolean
  })

  /**
 * Solicita a criação de um novo relatório no backend com base nos parâmetros fornecidos.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/task/create`,
 * contendo os parâmetros de configuração do relatório.  
 * 
 * Em caso de sucesso:
 * - Exibe uma notificação de sucesso via `toast`.
 * - Redireciona o usuário para a página `/relatorios`.
 * 
 * Em caso de falha:
 * - Se o backend retornar um erro (`resp.data.erro`), exibe a mensagem correspondente.
 * - Caso ocorra uma exceção na requisição, exibe uma mensagem genérica de erro.
 * 
 * @async
 * @param {any} params Objeto contendo os parâmetros necessários para gerar o relatório.  
 * Recomenda-se definir uma interface específica (`SolicitarRelatorioParams`) para maior clareza e segurança.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a solicitação e tratamento da resposta.
 */

  async handleSolicitarRelatorio(params: any) {
    try {
      let resp = await api.post("/api/relatorio/task/create", params);

      if (resp.data.ok) {
        toast.success('Relatório Solicitado', {});
        return true;
      } else {
        toast.error(resp.data.erro, {});
      }
    } catch (ex) {
      toast.error("Erro ao Solicitar Relatório, tente novamente mais tarde.", {});
    }
    return false;
  }




  async handleSolicitarRelatorioIntegracao(params: any) {
    try {
      let resp = await api.post("/api/integracao/create", params);

      if (resp.data.ok) {
        toast.success('Relatório Solicitado', {});
      } else {
        toast.error(resp.data.erro, {});
      }
    } catch (ex) {
      toast.error("Erro ao Solicitar Relatório, tente novamente mais tarde.", {});
    }
  }

  /**
 * Busca a lista paginada de relatórios solicitados.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/task/index`,
 * informando o número da página (`page`) e um limite fixo de 15 registros por página.
 * 
 * Em caso de sucesso:
 * - Atualiza `tasks_relatorio` com a lista de itens retornados.
 * - Atualiza `length_tasks_relatorio` com a contagem total de registros.
 * 
 * Em caso de falha:
 * - Exibe uma mensagem de erro genérica via `toast`.
 * 
 * @async
 * @param {number} paged Número da página que deve ser carregada.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a atualização da lista de relatórios.
 */

  async handleTasksPaged(paged: number) {
    try {
      let data = {
        page: paged,
        limit: 15
      }
      let resp = await api.post("/api/relatorio/task/index", data);

      this.obs.tasks_relatorio = [];
      this.obs.length_tasks_relatorio = 0

      if (resp.data.itens.length > 0) {
        this.obs.tasks_relatorio = resp.data.itens
        this.obs.length_tasks_relatorio = resp.data.count
      }
    } catch (ex) {
      toast.error("Erro ao Carregar Relatórios Solicitados, tente novamente mais tarde.", {});
    }
  }



  async handleTasksPagedIntegracao(paged: number) {
    try {
      let data = {
        page: paged,
        limit: 15
      }
      let resp = await api.post("/api/integracao/index", data);

      this.obs.tasks_relatorio = [];
      this.obs.length_tasks_relatorio = 0

      if (resp.data.itens.length > 0) {
        this.obs.tasks_relatorio = resp.data.itens
        this.obs.length_tasks_relatorio = resp.data.count
      }
    } catch (ex) {
      toast.error("Erro ao Carregar Relatórios Solicitados, tente novamente mais tarde.", {});
    }
  }
  /**
 * Salva as configurações ou dados de um relatório no backend.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/save`,
 * contendo os parâmetros do relatório a ser salvo.
 * 
 * Em caso de sucesso:
 * - Exibe uma mensagem de confirmação via `toast` informando que o relatório foi salvo.
 * 
 * Em caso de falha:
 * - Se o backend retornar uma mensagem de erro (`resp.data.erro`), ela será exibida.
 * - Caso ocorra um erro na requisição, exibe uma mensagem genérica de falha no salvamento.
 * 
 * @async
 * @param {any} params Objeto contendo as informações necessárias para salvar o relatório.  
 * Recomenda-se definir uma interface específica (`SalvarRelatorioParams`) para maior clareza e segurança de tipos.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a tentativa de salvamento.
 */

  async handleSaveRelatorio(params: any) {
    try {

      let resp = await api.post("/api/relatorio/save", params);

      if (resp.data.ok) {
        toast.success('Relatório Salvado', {});
      } else {
        toast.error(resp.data.erro, {});
      }
    } catch (ex) {
      toast.error("Erro ao Salvar Relatório, tente novamente mais tarde.", {});
    }
  }

  /**
 * Carrega a lista de relatórios disponíveis a partir do backend.
 * 
 * A função envia uma requisição `GET` para o endpoint `/api/relatorio/index`
 * e organiza os relatórios recebidos em categorias específicas:
 * - `nfe`: Notas Fiscais Eletrônicas (NF-E)
 * - `cte`: Conhecimentos de Transporte Eletrônicos (CT-E)
 * - `nfce`: Notas Fiscais de Consumidor Eletrônicas (NFC-E)
 * - `nfse`: Notas Fiscais de Serviços Eletrônicas (NFS-E)
 * 
 * Em caso de sucesso:
 * - Atualiza o estado observável `relatorios` com as listas de cada tipo.
 * 
 * Em caso de falha:
 * - Exibe a mensagem de erro retornada pelo backend.
 * - Se houver exceção na requisição, mostra uma mensagem genérica.
 * 
 * @async
 * @returns {Promise<void>} Retorna uma Promise resolvida após a atualização dos relatórios.
 */

  async handleRelatorios() {
    try {

      let resp = await api.get("/api/relatorio/index");

      if (resp.data.ok) {
        this.obs.relatorios = {
          nfe: (resp.data.relatorios["NF-E"] || []),
          cte: (resp.data.relatorios["CT-E"] || []),
          nfce: (resp.data.relatorios["NFC-E"] || []),
          nfse: (resp.data.relatorios["NFS-E"] || [])
        }
      } else {
        toast.error(resp.data.erro, {});
      }
    } catch (ex) {
      toast.error("Erro ao Carregar Relatórios", {});
    }
  }

  /**
 * Realiza o download de um relatório no formato especificado para a empresa informada.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/task/download`,
 * passando o `id` do relatório e o `format` desejado (exemplo: `"xlsx"`).
 * O backend retorna o arquivo no formato binário (`blob`), que é convertido e baixado no
 * navegador com o nome `{cnpj}_relatorio.{format}`.
 * 
 * Durante o processo, a propriedade observável `loading` é usada para indicar visualmente
 * que o download está em andamento.
 * 
 * Em caso de sucesso:
 * - O arquivo é baixado automaticamente no formato solicitado.
 * 
 * Em caso de falha:
 * - Exibe a mensagem de erro contida no cabeçalho (`headers.msg`), se presente.
 * - Caso contrário, mostra uma mensagem genérica de erro.
 * 
 * @async
 * @param {string} id Identificador único do relatório a ser baixado.
 * @param {string} cnpj CNPJ da empresa que será usado no nome do arquivo.
 * @param {string} format Formato do arquivo a ser baixado (ex.: `"xlsx"`).
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a tentativa de download.
 */

  async handleDownloadArquivoRelatorio(id: string, cnpj: string, format: string) {
    this.obs.loading = true

    try {

      let resp = await api.post(`/api/relatorio/task/download`, {id: id, format: format}, { responseType: 'blob' });

      if(resp.status == 200){

        const fileName = cnpj + "_relatorio." + format;

        downloadBinaryFile(resp.data, fileName)

      } else {

        toast.error(decodeUtf8String(resp.headers.msg), {});

      }

    } catch (ex) {

      toast.error('erro ao baixar o arquivo, tente novamente mais tarde ou contate o suporte.', {});

    }

    this.obs.loading = false
  }

 /**
 * Exclui um relatório existente no backend com base no seu identificador.
 * 
 * A função envia uma requisição `DELETE` para o endpoint `/api/relatorio/{id}`,
 * removendo o relatório informado.  
 * 
 * Antes de iniciar a exclusão, fecha o painel de relatórios salvos (`open_relatorios_salvos = false`).
 * 
 * Em caso de sucesso:
 * - Exibe mensagem de confirmação via `toast`.
 * - Limpa a lista atual de relatórios (`relatorios = []`).
 * - Recarrega a lista de relatórios chamando `handleRelatorios()`.
 * 
 * Em caso de falha:
 * - Se o backend retornar uma mensagem de erro (`resp.data.erro`), ela será exibida via `toast`.
 * - Caso ocorra exceção, não exibe mensagem extra (mas poderia ser ajustado para feedback do usuário).
 * 
 * @async
 * @param {string|number} id Identificador único do relatório que será excluído.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a tentativa de exclusão.
 */
  async handleDelete(id) {
    RelatorioStore.obs.open_relatorios_salvos = false
    try {
      let resp = await api.delete(`/api/relatorio/${id}`);
      if (resp.data.ok) {
        toast.success('Relatório Deletado', {});
        RelatorioStore.obs.relatorios = []
        this.handleRelatorios()
      } else {
        toast.error(resp.data.erro, {});
      }
    } catch (ex) { }
  }

  /**
 * Filtra e retorna a lista de cálculos válidos para a data atual.
 * 
 * A função:
 * 1. Obtém a data e hora atuais.
 * 2. Ordena a lista de cálculos pelo campo `nome`.
 * 3. Retorna apenas os cálculos que estejam dentro do intervalo de validade
 *    definido por `valido_de` e `valido_ate`.
 * 
 * Regras de filtragem:
 * - Se ambos `valido_de` e `valido_ate` forem `null`, o cálculo é considerado válido.
 * - Se a data atual estiver entre `valido_de` e `valido_ate`, o cálculo é válido.
 * - Se `valido_de` for `null` e a data atual for anterior ou igual a `valido_ate`, o cálculo é válido.
 * - Se a data atual for posterior ou igual a `valido_de` e `valido_ate` for `null`, o cálculo é válido.
 * 
 * @param {any} calcs Lista de cálculos a serem verificados.
 * @returns {ICalc[]} Lista de cálculos válidos ordenados por `nome`.
 */

  validForDate(calcs: any): ICalc[] {
    let date = moment(new Date()).format()
    return sortBy(calcs, "nome").filter(calc => {
      if (calc.valido_de == null && calc.valido_ate == null) {
        return true;
      }

      if (date >= moment(calc.valido_de).format() && date <= moment(calc.valido_ate).format()) {
        return true;
      }

      if (calc.valido_de == null && date <= moment(calc.valido_ate).format()) {
        return true;
      }

      if (date >= moment(calc.valido_de).format() && calc.valido_ate == null) {
        return true;
      }

      return false;
    });
  }

}

export const RelatorioStore = new _RelatorioStore();
